import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MeterIcon from "components/MeterIcon";
import Page from "components/Page";
import { getRecentMeters, rememberMeter } from "cookies";

function HomeForm({
    value,
    onChange,
    onSubmit,
    error,
    recentMeters,
}: {
    value: string;
    onChange: (value: string) => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
    error?: string;
    recentMeters: string[];
}) {
    return (
        <Page>
            <h1 className="text-engage-500 text-xl sm:text-2xl font-bold block mb-4">PPEX Sneak Peek</h1>
            <form onSubmit={onSubmit} className="py-2">
                <div className="flex justify-center my-3">
                    <MeterIcon />
                </div>
                <input
                    id="meter-number"
                    name="meter-number"
                    type="number"
                    inputMode="numeric"
                    autoComplete="off"
                    className="border border-gray-300 bg-gray-50 w-full text-center rounded-lg p-3 text-lg"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Meter number"
                />
                {error && <div className="text-red-500 text-sm mt-3">{error}</div>}
                <button
                    type="submit"
                    className="mt-6 w-full bg-engage-400 hover:bg-engage-500 active:bg-engage-600 text-white font-bold py-3 px-4 rounded-lg border border-engage-500"
                >
                    Find my info
                </button>

                {recentMeters.length > 0 && (
                    <div className="mt-8" id="meter-list">
                        <div className="border-b border-b-gray-200 mb-4 text-engage-500 font-bold text-sm">
                            Recent meters
                        </div>
                        {recentMeters.map((meter) => (
                            <a key={meter} href={`/meters/${meter}`}>{meter}</a>
                        ))}
                    </div>
                )}
            </form>
        </Page>
    );
}

export default function HomePage() {
    const [meterNumber, setMeterNumber] = useState("");
    const [recentMeters, setRecentMeters] = useState<string[]>([]);
    const nav = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        setRecentMeters(getRecentMeters());
    }, []);

    useEffect(() => {
        const legacyMeter = searchParams.get("meter-number");
        if (legacyMeter) {
            nav(`/meters/${legacyMeter}`, { replace: true });
        }
    }, [nav, searchParams]);

    function onMeterSearch(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!meterNumber.trim()) {
            return;
        }
        rememberMeter(meterNumber.trim());
        nav(`/meters/${meterNumber.trim()}`);
    }

    return (
        <HomeForm
            value={meterNumber}
            onChange={setMeterNumber}
            onSubmit={onMeterSearch}
            recentMeters={recentMeters}
        />
    );
}

export function HomePageWithError({ meterNumber, error }: { meterNumber: string; error: string }) {
    const [value, setValue] = useState(meterNumber);
    const [recentMeters, setRecentMeters] = useState<string[]>([]);
    const nav = useNavigate();

    useEffect(() => {
        setRecentMeters(getRecentMeters());
    }, []);

    function onMeterSearch(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!value.trim()) {
            return;
        }
        rememberMeter(value.trim());
        nav(`/meters/${value.trim()}`);
    }

    return (
        <HomeForm
            value={value}
            onChange={setValue}
            onSubmit={onMeterSearch}
            error={error}
            recentMeters={recentMeters}
        />
    );
}
